/**
 * Modelo audiovisual canónico (versión 1) — audio del timeline.
 *
 * Espejo exacto de `backend/app/render/audio_model.py`: mismos nombres,
 * unidades y defaults. El preview y la exportación NO pueden interpretar el
 * estado sonoro de maneras distintas.
 */
export const AUDIO_MODEL_VERSION = 1;

export const VISUAL_TRACK_IDS = ['base', 'v1', 'v2'];
export const TRACK_IDS = ['base', 'v1', 'v2', 'audio'];

export const CLIP_AUDIO_DEFAULTS = {
  source_audio_enabled: true,
  muted: false,
  volume: 1.0,
  audio_fade_in: 0,
  audio_fade_out: 0,
};

export const TRACK_DEFAULTS = { muted: false, solo: false, volume: 1.0 };
export const MIX_DEFAULTS = { master_muted: false, master_volume: 1.0 };

const num = (value, fallback, low = 0, high = 2) => {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(low, Math.min(high, parsed));
};

const bool = (value, fallback) => (value === undefined || value === null ? fallback : !!value);

export const normalizeClipAudio = (clip = {}) => ({
  source_audio_enabled: bool(clip.source_audio_enabled, CLIP_AUDIO_DEFAULTS.source_audio_enabled),
  muted: bool(clip.muted, CLIP_AUDIO_DEFAULTS.muted),
  volume: num(clip.volume, CLIP_AUDIO_DEFAULTS.volume),
  audio_fade_in: num(clip.audio_fade_in, 0, 0, 600),
  audio_fade_out: num(clip.audio_fade_out, 0, 0, 600),
});

export const normalizeTrack = (track = {}) => ({
  muted: bool(track.muted, TRACK_DEFAULTS.muted),
  solo: bool(track.solo, TRACK_DEFAULTS.solo),
  volume: num(track.volume, TRACK_DEFAULTS.volume),
});

export const normalizeTracks = (tracks = {}) =>
  TRACK_IDS.reduce((acc, id) => ({ ...acc, [id]: normalizeTrack(tracks[id]) }), {});

export const normalizeMix = (mix = {}) => ({
  master_muted: bool(mix.master_muted, MIX_DEFAULTS.master_muted),
  master_volume: num(mix.master_volume, MIX_DEFAULTS.master_volume),
});

export const soloActive = (tracks = {}) =>
  TRACK_IDS.some((id) => normalizeTrack(tracks[id]).solo);

/** Ganancia final (0 = silencio) de un clip dentro de la mezcla. */
export const effectiveGain = (clip, trackId, tracks = {}, mix = {}, { isVisual = true } = {}) => {
  const clipAudio = normalizeClipAudio(clip || {});
  const normTracks = normalizeTracks(tracks);
  const normMix = normalizeMix(mix);
  const track = normTracks[trackId] || normalizeTrack();

  if (normMix.master_muted || track.muted || clipAudio.muted) return 0;
  if (soloActive(normTracks) && !track.solo) return 0;
  if (isVisual && !clipAudio.source_audio_enabled) return 0;
  return normMix.master_volume * track.volume * clipAudio.volume;
};

/** Volumen aplicable a un elemento HTMLMedia (que sólo acepta 0..1). */
export const previewVolume = (gain) => Math.max(0, Math.min(1, gain));
